import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  Component,
  DestroyRef,
  ElementRef,
  Inject,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { debounceTime, distinctUntilChanged, startWith } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface TextLogViewerData {
  fileName: string;
  content: string;
}

@Component({
  selector: 'app-text-log-viewer',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
  ],
  templateUrl: './text-log-viewer.component.html',
  styleUrl: './text-log-viewer.component.scss',
})
export class TextLogViewerComponent implements OnInit, AfterViewChecked {
  private readonly destroyRef = inject(DestroyRef);
  private pendingScrollToActive = false;

  @ViewChild('contentScroll') contentScroll?: ElementRef<HTMLElement>;

  readonly searchControl = new FormControl('', { nonNullable: true });
  readonly displayText: string;

  renderedHtml = '';
  matchCount = 0;
  activeMatchIndex = -1;

  get showNoMatches(): boolean {
    return this.searchControl.value.trim().length > 0 && this.matchCount === 0;
  }

  get matchPositionLabel(): string {
    if (this.matchCount === 0 || this.activeMatchIndex < 0) {
      return '';
    }
    return `${this.activeMatchIndex + 1} / ${this.matchCount}`;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: TextLogViewerData,
    private readonly dialogRef: MatDialogRef<TextLogViewerComponent>
  ) {
    this.displayText = this.prepareDisplayText(data?.content ?? '');
  }

  ngOnInit(): void {
    this.renderWithHighlight('');
    this.searchControl.valueChanges
      .pipe(
        startWith(this.searchControl.value),
        debounceTime(120),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((query) => {
        this.activeMatchIndex = -1;
        this.renderWithHighlight(query);
      });
  }

  ngAfterViewChecked(): void {
    if (!this.pendingScrollToActive) {
      return;
    }
    this.pendingScrollToActive = false;
    this.scrollActiveMatchIntoView();
  }

  findNext(): void {
    if (this.matchCount === 0) {
      return;
    }
    this.activeMatchIndex = (this.activeMatchIndex + 1) % this.matchCount;
    this.renderWithHighlight(this.searchControl.value);
  }

  findPrevious(): void {
    if (this.matchCount === 0) {
      return;
    }
    this.activeMatchIndex =
      (this.activeMatchIndex - 1 + this.matchCount) % this.matchCount;
    this.renderWithHighlight(this.searchControl.value);
  }

  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.shiftKey ? this.findPrevious() : this.findNext();
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  /**
   * Decode transport escapes and insert line breaks before repeated log entries
   * so MontageLogEntry blocks are easier to read (display-only, not parsed).
   */
  private prepareDisplayText(raw: string): string {
    let text = String(raw ?? '');

    if (text.includes('\\n') || text.includes('\\r') || text.includes('\\t')) {
      text = text
        .replace(/\\r\\n/g, '\n')
        .replace(/\\n/g, '\n')
        .replace(/\\r/g, '\n')
        .replace(/\\t/g, '\t');
    }

    text = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    // Each MontageLogEntry should start on its own line when file stores them inline.
    text = text.replace(/([^\n])MontageLogEntry\(/g, '$1\nMontageLogEntry(');

    return text;
  }

  private renderWithHighlight(query: string): void {
    const trimmed = query.trim();
    if (!trimmed) {
      this.matchCount = 0;
      this.activeMatchIndex = -1;
      this.renderedHtml = this.escapeHtml(this.displayText);
      return;
    }

    const pattern = new RegExp(`(${this.escapeRegExp(trimmed)})`, 'gi');
    const matches = this.displayText.match(pattern);
    this.matchCount = matches?.length ?? 0;

    if (this.matchCount === 0) {
      this.activeMatchIndex = -1;
      this.renderedHtml = this.escapeHtml(this.displayText);
      return;
    }

    if (this.activeMatchIndex < 0 || this.activeMatchIndex >= this.matchCount) {
      this.activeMatchIndex = 0;
    }

    let currentMatch = 0;
    const highlighted = this.displayText
      .split(pattern)
      .map((part, index) => {
        if (index % 2 === 1) {
          const isActive = currentMatch === this.activeMatchIndex;
          const mark = `<mark class="text-log-match${
            isActive ? ' text-log-match--active' : ''
          }">${this.escapeHtml(part)}</mark>`;
          currentMatch += 1;
          return mark;
        }
        return this.escapeHtml(part);
      })
      .join('');

    this.renderedHtml = highlighted;
    if (this.activeMatchIndex >= 0) {
      this.pendingScrollToActive = true;
    }
  }

  private scrollActiveMatchIntoView(): void {
    const container = this.contentScroll?.nativeElement;
    if (!container) {
      return;
    }
    const active = container.querySelector(
      '.text-log-match--active'
    ) as HTMLElement | null;
    active?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}
